import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button, Checkbox,
  FormControlLabel, CircularProgress, Stack, Chip, IconButton,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { getPendingAgreements, acceptAgreement } from '../services/agreementService';
import { getUserData } from '../services/storageService';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';

const AgreementModal = () => {
  const [agreements, setAgreements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    try {
      const res = await getPendingAgreements();
      if (res.success) setAgreements(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    const ag = agreements[currentIndex];
    if (!ag) return;

    setSubmitting(true);
    try {
      const res = await acceptAgreement(ag._id);
      if (res.success) {
        const remaining = agreements.filter((_, i) => i !== currentIndex);
        setAgreements(remaining);
        setCurrentIndex(0);
        setChecked(false);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || agreements.length === 0) return null;

  const current = agreements[currentIndex];
  const user = getUserData();
  const token = user?.userId || user?.token;
  const pdfUrl = `${API_URL}/agreements/${current._id}/file?token=${token}`;

  return (
    <Dialog
      open={true}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', height: '90vh', maxHeight: 750 } }}
      disableEscapeKeyDown
    >
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #059669 100%)',
        px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <PictureAsPdfIcon sx={{ color: 'white', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
            Điều khoản & Hợp đồng
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Vui lòng đọc và đồng ý trước khi sử dụng
          </Typography>
        </Box>
        {agreements.length > 1 && (
          <Chip label={`${currentIndex + 1}/${agreements.length}`} size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
        )}
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Title bar */}
        <Box sx={{ px: 3, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700} color="#1e293b" sx={{ flex: 1 }}>
              {current.title}
            </Typography>
            <Chip label={`v${current.version}`} size="small" sx={{ fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1' }} />
          </Stack>
          {current.description && (
            <Typography variant="caption" color="text.secondary">{current.description}</Typography>
          )}
        </Box>

        {/* PDF viewer via proxy - browser renders PDF natively in iframe */}
        <Box sx={{ flex: 1 }}>
          <iframe
            src={pdfUrl}
            title={current.title}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                sx={{ '&.Mui-checked': { color: '#059669' } }}
              />
            }
            label={
              <Typography variant="body2" fontWeight={600} color="#334155">
                Tôi đã đọc và đồng ý với điều khoản này
              </Typography>
            }
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {agreements.length > 1 && (
              <>
                <IconButton
                  disabled={currentIndex === 0}
                  onClick={() => { setCurrentIndex(currentIndex - 1); setChecked(false); }}
                  sx={{ border: '1px solid #e2e8f0' }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton
                  disabled={currentIndex === agreements.length - 1}
                  onClick={() => { setCurrentIndex(currentIndex + 1); setChecked(false); }}
                  sx={{ border: '1px solid #e2e8f0' }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              disabled={!checked || submitting}
              onClick={handleAccept}
              startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              sx={{
                borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3,
                bgcolor: '#059669', '&:hover': { bgcolor: '#047857' },
              }}
            >
              Đồng ý
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AgreementModal;
